import React, { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "@shadcn-in-nx/ui/button";
import { Input } from "@shadcn-in-nx/ui/input";

import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
    DropdownMenuSeparator,
    DropdownMenuLabel,
} from "@shadcn-in-nx/ui/dropdown-menu";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@shadcn-in-nx/ui/dialog";
import { Badge } from "@shadcn-in-nx/ui/badge";

import {
    Plus,
    Settings,
    Puzzle,
    Sparkles,
    ChevronDown,
    Check,
    ExternalLink,
    Search,
    X,
    Database,
    Crown,
} from "lucide-react";
import { motion } from "framer-motion";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";
import { useLocation, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Alert, AlertDescription, AlertTitle } from "@shadcn-in-nx/ui/alert";
import Icon from "../../components/Icon";
import { INTEGRATION_TYPES } from "../../Integrations/Integrations";
import { NewIntegrationModal } from "../../Integrations/NewIntegrationModal";
import { WhatsAppEmbeddedModal } from "../../Integrations/WhatsAppEmbeddedModal";
import { SKILLS_AVAILABLE } from "../../skills";
import { useChatContext } from "../ChatContext";
import { useWorkflowGeneration } from "../../context/WorkflowGenerationContext";
import { useWorkflowExecution } from "../../context/WorkflowExecutionContext";
import ChatInput from "./ChatInput";
import { Bluelagoon } from "@bluelagoon";
import { useWorkflow } from "../../context/WorkflowContext";

// Mock AI recommendations - these would come from an AI service
const AI_RECOMMENDATIONS = [
    // {
    //     id: "data-automation",
    //     title: "Data Automation",
    //     description: "Automate data processing and analysis workflows",
    //     icon: <Bot className="h-4 w-4" />,
    //     type: "automation",
    // },
    // {
    //     id: "email-assistant",
    //     title: "Email Assistant",
    //     description: "AI-powered email management and responses",
    //     icon: <Mail className="h-4 w-4" />,
    //     type: "skill",
    // },
    // {
    //     id: "document-processor",
    //     title: "Document Processor",
    //     description: "Extract and analyze content from documents",
    //     icon: <FileText className="h-4 w-4" />,
    //     type: "automation",
    // },
    // {
    //     id: "web-scraper",
    //     title: "Web Scraper",
    //     description: "Collect data from websites automatically",
    //     icon: <Globe className="h-4 w-4" />,
    //     type: "skill",
    // },
];

// Available integrations from the codebase
const AVAILABLE_INTEGRATIONS = INTEGRATION_TYPES;
export default function ChatMainInput({
    disabled = false,
    initialMessage = "",
    onMessageSet,
    minHeight,
    placeholderOverride,
    messagePrefix,
}) {
    const { workflow, workflowId } = useWorkflow();
    const isAppV2 = workflow?.workflowType === "APP_V2";
    const isAutomation = workflow?.workflowType === "AUTOMATION";
    const hideActionButtons = isAppV2 || isAutomation;
    const location = useLocation();
    const { t } = useTranslation();

    const [showOAuthDialog, setShowOAuthDialog] = useState(false);

    const [integrations, setIntegrations] = useState(AVAILABLE_INTEGRATIONS);
    const [recommendations, setRecommendations] = useState([]);
    const [showNewIntegrationModal, setShowNewIntegrationModal] =
        useState(false);
    const [selectedIntegrationForSetup, setSelectedIntegrationForSetup] =
        useState(null);
    const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);

    const [skilsInChatConversation, setSkilsInChatConversation] = useState([]);
    const [integrationsInChatConversation, setIntegrationsInChatConversation] =
        useState([]);

    const [forceModel, setForceModel] = useState(() => {
        return localStorage.getItem("preferredModel") || "auto";
    });

    useEffect(() => {
        if (forceModel && forceModel !== "auto") {
            localStorage.setItem("preferredModel", forceModel);
        } else {
            localStorage.removeItem("preferredModel");
        }
    }, [forceModel]);
    // Search state
    const [skillsSearchTerm, setSkillsSearchTerm] = useState("");
    const [integrationsSearchTerm, setIntegrationsSearchTerm] = useState("");
    const [currentKeyword, setCurrentKeyword] = useState(1);

    // Refs for auto-focus
    const skillsSearchRef = useRef(null);
    const integrationsSearchRef = useRef(null);

    // No explicit open state needed; we focus inputs on open via refs

    // Use React Router navigation
    const navigate = useNavigate();

    // Use context for selected integration and skills
    const {
        selectIntegration,
        addSkill,
        ensureWorkflowExists,
        setPendingMessage,
        selectedSkills,
        selectedIntegration,
        setSelectedIntegration,
        uploadFiles,

        setPendingFiles,
        isCurrentEnvironmentLocked,
        selectedEnvironment,

        // Local staged files (no upload until send)
        localStagedFiles,
        stageFilesLocally,
        uploadLocalStagedFiles,
        clearLocalStagedFiles,
        clearStagedFiles,
        componentRef,
        clearComponentRef,
    } = useChatContext();

    // Use context for chat generation
    const {
        handleChat,
        isGeneratingCode,
        isChatLoading,
        chatMessages,
        cancelBackgroundTask,
        chatMode: mode,
        setChatMode: setMode,
    } = useWorkflowGeneration();

    // Use context for workflow execution to get database information
    const { databasesResponse, recentDatabases, handleResetRecentDatabases } =
        useWorkflowExecution();

    // Fetch user integrations
    const { call: fetchIntegrations, response: integrationsResponse } =
        useBluelagoon({
            method: "integrationsList",
        });

    // Get active database
    const activeDatabase = useMemo(() => {
        return databasesResponse?.data?.databases?.find(
            (db) => db.isActiveWorkflowDatabase
        );
    }, [databasesResponse]);

    // OAuth URL generation
    const { call: getAuthUrl } = useBluelagoon({
        method: "getGoogleAuthUrl", // This would be dynamic based on integration
    });

    useEffect(() => {
        // Fetch user's integrations on mount
        fetchIntegrations({ limit: 100 });

        // Generate AI recommendations
        generateRecommendations();
    }, [fetchIntegrations]);

    useEffect(() => {
        if (integrationsResponse?.data) {
            // Update integrations with user's connected status
            const userIntegrations = integrationsResponse.data;
            setIntegrations((prevIntegrations) => {
                // Check if we actually need to update to avoid infinite loops
                const newIntegrations = prevIntegrations.map((integration) => {
                    const userIntegration = userIntegrations.find(
                        (ui) => ui.type === integration.id
                    );
                    return {
                        ...integration,
                        connected: !!userIntegration,
                        userIntegrationId: userIntegration?.id,
                        userIntegrationName: userIntegration?.name,
                    };
                });

                // Simple deep comparison to avoid unnecessary updates
                if (
                    JSON.stringify(prevIntegrations) ===
                    JSON.stringify(newIntegrations)
                ) {
                    return prevIntegrations;
                }
                return newIntegrations;
            });
        }
    }, [integrationsResponse?.data]);

    const generateRecommendations = () => {
        // Simulate AI generating recommendations
        const shuffled = [...AI_RECOMMENDATIONS].sort(
            () => 0.5 - Math.random()
        );
        setRecommendations(shuffled.slice(0, 2));
    };

    const handleSendMessage = React.useCallback(
        async (messageData) => {
            // Check if there's anything to send (text, files, or local staged files)
            const hasLocalFiles = localStagedFiles.length > 0;
            if (
                !messageData.text.trim() &&
                messageData.files.length === 0 &&
                messageData.skills.length === 0 &&
                !hasLocalFiles
            )
                return;

            try {
                // Ensure workflow exists before sending message
                const currentWorkflow = await ensureWorkflowExists();
                const skillsToSend = selectedSkills.filter(
                    (skill) => !skilsInChatConversation.includes(skill.name)
                );
                const integrationsToSend =
                    selectedIntegration &&
                    integrationsInChatConversation.includes(
                        selectedIntegration?.name
                    )
                        ? []
                        : selectedIntegration
                        ? [selectedIntegration]
                        : [];

                // Build component reference prefix if a component is referenced
                let componentRefPrefix = "";
                if (componentRef) {
                    const refParts = [
                        `Component: ${componentRef.componentName}`,
                    ];
                    if (componentRef.filePath)
                        refParts.push(`File: ${componentRef.filePath}`);
                    if (componentRef.elementTag)
                        refParts.push(`Element: <${componentRef.elementTag}>`);
                    if (componentRef.elementText)
                        refParts.push(
                            `Text: "${componentRef.elementText.substring(
                                0,
                                60
                            )}"`
                        );
                    componentRefPrefix = `[Referencing ${refParts.join(
                        ", "
                    )}]\n`;
                }

                const message =
                    componentRefPrefix +
                    messageData.text +
                    " " +
                    skillsToSend?.map((skill) => `@[${skill.name}]`).join(" ") +
                    " " +
                    integrationsToSend
                        ?.map(
                            (integration) =>
                                `@[${
                                    integration.userIntegrationName ||
                                    integration.name
                                }]`
                        )
                        .join(" ");

                setSkilsInChatConversation((prev) => [
                    ...prev,
                    ...skillsToSend.map((skill) => skill.name),
                ]);

                setIntegrationsInChatConversation((prev) => [
                    ...prev,
                    ...integrationsToSend.map(
                        (integration) => integration.name
                    ),
                ]);

                // Check if we need to navigate (new workflow or not on chat page)
                const needsNavigation =
                    currentWorkflow.newWorkflow ||
                    !location.pathname.includes(`${currentWorkflow.id}/chat`);

                if (needsNavigation) {
                    // Navigate first, then handle files and message on the new page
                    // Set pending files from local staged files
                    if (hasLocalFiles) {
                        const filesToUpload = localStagedFiles.map(
                            (f) => f.file
                        );
                        setPendingFiles(filesToUpload);
                        clearLocalStagedFiles();
                    }
                    setPendingMessage(message);
                    // Clear component reference before navigation to prevent it persisting
                    if (componentRef) clearComponentRef();
                    navigate(`/chat/${currentWorkflow.id}/chat`);
                    return;
                }

                // We're on the chat page - upload local files first, then send
                let uploadedFiles = [];
                if (hasLocalFiles) {
                    // Upload local staged files and get the uploaded file objects
                    uploadedFiles = await uploadLocalStagedFiles();
                }

                // Combine any pre-existing staged files with newly uploaded files
                const allFiles = [
                    ...(messageData.files || []),
                    ...uploadedFiles,
                ];

                // Note: Human support ticket count is incremented in WorkflowGenerationContext
                // only when a NEW ticket is created (not for replies)

                // Send message using the new chat generation context with staged files
                await handleChat(
                    message,
                    chatMessages,
                    allFiles,
                    [],
                    mode,
                    forceModel,
                    selectedEnvironment
                );

                // Clear any files that were added to stagedFiles during upload
                clearStagedFiles();

                // Clear component reference after sending
                if (componentRef) clearComponentRef();
            } catch (error) {
                console.log("error", error);
                toast.error("Failed to send message: " + error?.message);
                console.error("Error sending message:", error);
            }
        },
        [
            selectedSkills,
            skilsInChatConversation,
            integrationsInChatConversation,
            selectedIntegration,
            handleChat,
            chatMessages,
            ensureWorkflowExists,
            location,
            navigate,
            setPendingMessage,
            setPendingFiles,
            mode,
            forceModel,
            selectedEnvironment,
            localStagedFiles,
            uploadLocalStagedFiles,
            clearLocalStagedFiles,
            clearStagedFiles,
            componentRef,
            clearComponentRef,
        ]
    );

    const MAX_IMAGE_FILES = 4;

    const handleFileSelect = React.useCallback(
        async (e) => {
            let files = Array.from(e.target.files);
            if (files.length > 0) {
                // Cap image files to prevent server-side event loop blocking
                const imageFiles = files.filter((f) =>
                    f.type?.startsWith("image/")
                );
                const nonImageFiles = files.filter(
                    (f) => !f.type?.startsWith("image/")
                );
                if (imageFiles.length > MAX_IMAGE_FILES) {
                    toast.error(
                        t("common.maxImageFiles", {
                            defaultValue: `You can attach up to ${MAX_IMAGE_FILES} images at a time. Only the first ${MAX_IMAGE_FILES} will be uploaded.`,
                            count: MAX_IMAGE_FILES,
                        })
                    );
                    files = [
                        ...imageFiles.slice(0, MAX_IMAGE_FILES),
                        ...nonImageFiles,
                    ];
                }

                // If we already have a workflow and are on the chat page, upload immediately
                if (
                    workflow &&
                    location.pathname.includes(`${workflow.id}/chat`)
                ) {
                    uploadFiles(files);
                } else {
                    // Otherwise, stage files locally - no workflow creation, no navigation
                    // Files will be uploaded when user sends the message
                    stageFilesLocally(files);
                    toast.success(`${files.length} file(s) ready to send`);
                }
            }
            // Reset the input
            e.target.value = "";
        },
        [uploadFiles, stageFilesLocally, workflow, location.pathname, t]
    );

    const handleRecommendationClick = (recommendation) => {
        toast.success(`Starting ${recommendation.title}...`);
        // Here you would typically navigate to the workflow or start the automation
    };

    const handleIntegrationSelect = (integration) => {
        if (integration.connected) {
            selectIntegration(integration);
        } else {
            // Special handling for WhatsApp integrations
            if (integration.id === "whatsapp") {
                setShowWhatsAppModal(true);
            } else {
                // Show NewIntegrationModal for other non-connected integrations
                setSelectedIntegrationForSetup(integration);
                setShowNewIntegrationModal(true);
            }
        }
    };

    const handleOAuthFlow = async () => {
        if (!selectedIntegration) return;

        try {
            if (selectedIntegration.hasOAuth) {
                // For OAuth integrations, redirect to OAuth URL
                const response = await getAuthUrl({
                    scopes: ["read", "write"], // This would be integration-specific
                });

                if (response?.data?.url) {
                    window.open(
                        response.data.url,
                        "_blank",
                        "noopener,noreferrer"
                    );
                    toast.success(
                        "Please complete the authorization in the new window"
                    );
                }
            } else {
                // For API key integrations, show configuration form
                toast.info("Configuration form would open here");
                // You would show a form to collect API keys, etc.
            }
        } catch (error) {
            toast.error("Failed to start OAuth flow");
            console.error(error);
        }

        setShowOAuthDialog(false);
        setSelectedIntegration(null);
    };

    const handleSkillSelect = (skill) => {
        addSkill(skill);
    };

    const handleNewIntegrationSuccess = (newIntegration) => {
        // For WhatsApp integrations, handle differently since there's no selectedIntegrationForSetup
        if (newIntegration.type === "whatsapp") {
            // Find the WhatsApp integration type from INTEGRATION_TYPES
            const whatsappIntegrationType = INTEGRATION_TYPES.find(
                (type) => type.id === "whatsapp"
            );

            const updatedIntegration = {
                ...whatsappIntegrationType,
                connected: true,
                userIntegrationId: newIntegration.id,
                userIntegrationName: newIntegration.name,
            };

            setIntegrations((prevIntegrations) =>
                prevIntegrations.map((integration) =>
                    integration.id === "whatsapp"
                        ? updatedIntegration
                        : integration
                )
            );

            // Automatically select the newly connected integration for chat
            selectIntegration(updatedIntegration);

            toast.success(
                `${newIntegration.name} WhatsApp integration connected and selected!`
            );

            // Close the WhatsApp modal
            setShowWhatsAppModal(false);

            // Fetch fresh integrations to update the list
            fetchIntegrations();
        } else {
            // Update the integrations list to mark the new integration as connected
            const updatedIntegration = {
                ...selectedIntegrationForSetup,
                connected: true,
                userIntegrationId: newIntegration.id,
            };

            setIntegrations((prevIntegrations) =>
                prevIntegrations.map((integration) =>
                    integration.id === newIntegration.type
                        ? updatedIntegration
                        : integration
                )
            );

            // Automatically select the newly connected integration for chat
            selectIntegration(updatedIntegration);

            toast.success(
                `${
                    newIntegration.name || selectedIntegrationForSetup?.name
                } integration connected and selected!`
            );

            // Close the modal
            setShowNewIntegrationModal(false);
            setSelectedIntegrationForSetup(null);
        }
    };

    const handleNewIntegrationClose = () => {
        setShowNewIntegrationModal(false);
        setSelectedIntegrationForSetup(null);
    };

    const connectedIntegrations = integrations.filter((i) => i.connected);
    const availableIntegrations = integrations.filter((i) => !i.connected);

    React.useEffect(() => {
        setCurrentKeyword((prev) => prev + 1);
    }, [workflow?.id]);

    return (
        <>
            {/* Environment Locked Alert */}
            {isCurrentEnvironmentLocked && (
                <div
                    className="px-4 py-3 bg-yellow-50 border-b border-yellow-200 rounded-t-xl shadow-lg shadow-yellow-100/60 border-t border-x border-yellow-200"
                    style={{
                        borderBottomLeftRadius: 0,
                        borderBottomRightRadius: 0,
                        marginBottom: -10,
                    }}
                >
                    <Alert className="bg-transparent border-0 px-4 py-0 flex items-center">
                        <Crown className="h-4 w-4 text-yellow-600 mr-2 flex-shrink-0" />
                        <div>
                            <AlertTitle className="text-yellow-800 text-sm">
                                {t(
                                    "componentCodeViewer.environmentLockedTitle"
                                ) || "Environment Locked"}
                            </AlertTitle>
                            <AlertDescription className="text-yellow-700 text-xs mt-0.5">
                                {t(
                                    "componentCodeViewer.environmentLockedMessage"
                                ) ||
                                    "This environment is locked. Switch to a development environment to make changes."}
                            </AlertDescription>
                        </div>
                    </Alert>
                </div>
            )}

            {/* Chat Input */}
            <ChatInput
                onSendMessage={handleSendMessage}
                isTyping={isGeneratingCode || isChatLoading}
                cancelBackgroundTask={cancelBackgroundTask}
                isGeneratingCode={isGeneratingCode}
                handleFileSelect={handleFileSelect}
                currentKeyword={currentKeyword}
                disabled={disabled || workflow?.status === "ARCHIVED"}
                initialMessage={initialMessage}
                onMessageSet={onMessageSet}
                minHeight={minHeight}
                mode={mode}
                setMode={setMode}
                setForceModel={setForceModel}
                forceModel={forceModel}
                placeholderOverride={placeholderOverride}
                messagePrefix={messagePrefix}
            />

            {/* Active Database Indicator */}

            {/* Action Buttons Row - hidden for APP_V2 and AUTOMATION */}
            {!hideActionButtons && (
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.1 }}
                    className="flex items-center justify-between"
                >
                    {/* Add Skills Button (Left) */}
                    <DropdownMenu
                        onOpenChange={(open) => {
                            if (open) {
                                setTimeout(
                                    () => skillsSearchRef.current?.focus(),
                                    0
                                );
                            }
                        }}
                    >
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2 hover:bg-gray-50"
                                disabled={disabled}
                            >
                                <Plus className="h-4 w-4 block md:hidden lg:hidden" />
                                <span className="hidden lg:inline">
                                    {t("chatInput.addSkills")}
                                </span>
                                <span className="lg:hidden">
                                    {t("chatInput.skills")}
                                </span>
                                <ChevronDown className="h-3 w-3 " />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="start"
                            className="w-80 max-h-96 overflow-y-auto"
                        >
                            <DropdownMenuLabel>
                                {t("chatInput.availableSkills")}
                            </DropdownMenuLabel>
                            <DropdownMenuSeparator />
                            <div className="p-2">
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <Input
                                        ref={skillsSearchRef}
                                        value={skillsSearchTerm}
                                        onChange={(e) =>
                                            setSkillsSearchTerm(e.target.value)
                                        }
                                        placeholder={t(
                                            "chatInput.searchSkills"
                                        )}
                                        className="pl-8 h-8"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <DropdownMenuSeparator />
                            {SKILLS_AVAILABLE.filter((skill) => {
                                const q = skillsSearchTerm.toLowerCase();
                                return (
                                    (skill.name || "")
                                        .toLowerCase()
                                        .includes(q) ||
                                    (skill.description || "")
                                        .toLowerCase()
                                        .includes(q) ||
                                    (skill.id || "").toLowerCase().includes(q)
                                );
                            })
                                .filter((skill) => !isAppV2 || !skill.isTrigger)
                                .map((skill) => (
                                    <DropdownMenuItem
                                        key={skill.id}
                                        onClick={() => handleSkillSelect(skill)}
                                        className="flex items-start gap-3 cursor-pointer p-3"
                                    >
                                        <span className="text-lg flex-shrink-0 mt-0.5">
                                            {skill.icon}
                                        </span>
                                        <div className="flex-1 min-w-0">
                                            <p className="font-medium text-gray-900 truncate">
                                                {skill.name}
                                            </p>
                                            <p className="text-xs text-gray-500 line-clamp-2">
                                                {
                                                    skill.description.split(
                                                        "\n"
                                                    )[0]
                                                }
                                            </p>
                                            {skill.isTrigger && (
                                                <Badge
                                                    variant="secondary"
                                                    className="mt-1 text-xs"
                                                >
                                                    {t("chatInput.trigger")}
                                                </Badge>
                                            )}
                                        </div>
                                    </DropdownMenuItem>
                                ))}
                        </DropdownMenuContent>
                    </DropdownMenu>

                    {/* AI Recommendations (Center) */}
                    <div className="flex gap-2 flex-1 justify-center">
                        {recommendations.map((rec, index) => (
                            <motion.div
                                key={rec.id}
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                transition={{ delay: 0.2 + index * 0.1 }}
                            >
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="flex items-center gap-2 bg-gradient-to-r from-blue-50 to-purple-50 border-blue-200 hover:from-blue-100 hover:to-purple-100 text-blue-700"
                                    onClick={() =>
                                        handleRecommendationClick(rec)
                                    }
                                >
                                    {rec.icon}
                                    <span className="hidden sm:inline font-medium">
                                        {rec.title}
                                    </span>
                                    <Sparkles className="h-3 w-3 text-yellow-500" />
                                </Button>
                            </motion.div>
                        ))}
                    </div>

                    {/* Integration Dropdown (Right) */}
                    <DropdownMenu
                        onOpenChange={(open) => {
                            if (open) {
                                setTimeout(
                                    () =>
                                        integrationsSearchRef.current?.focus(),
                                    0
                                );
                            }
                        }}
                    >
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                size="sm"
                                className="flex items-center gap-2 hover:bg-gray-50"
                                disabled={disabled}
                            >
                                <Puzzle className="h-4 w-4 block md:hidden lg:hidden" />
                                <span className="hidden lg:inline">
                                    {t("chatInput.addIntegration")}
                                </span>
                                <span className="lg:hidden">
                                    {t("chatInput.integrations")}
                                </span>
                                <ChevronDown className="h-3 w-3" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent
                            align="end"
                            className="w-64 max-h-96 overflow-y-auto"
                        >
                            <div className="p-2">
                                <div className="relative">
                                    <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <Input
                                        ref={integrationsSearchRef}
                                        value={integrationsSearchTerm}
                                        onChange={(e) =>
                                            setIntegrationsSearchTerm(
                                                e.target.value
                                            )
                                        }
                                        placeholder={t(
                                            "chatInput.searchIntegrations"
                                        )}
                                        className="pl-8 h-8"
                                        autoFocus
                                    />
                                </div>
                            </div>
                            <DropdownMenuSeparator />
                            {connectedIntegrations.filter((integration) => {
                                const q = integrationsSearchTerm.toLowerCase();
                                return (
                                    (integration.name || "")
                                        .toLowerCase()
                                        .includes(q) ||
                                    (integration.category || "")
                                        .toLowerCase()
                                        .includes(q) ||
                                    (integration.id || "")
                                        .toLowerCase()
                                        .includes(q)
                                );
                            }).length > 0 && (
                                <>
                                    <DropdownMenuLabel className="flex items-center gap-2">
                                        <Check className="h-4 w-4 text-green-600" />
                                        {t("chatInput.connected")}
                                    </DropdownMenuLabel>
                                    {connectedIntegrations
                                        .filter((integration) => {
                                            const q =
                                                integrationsSearchTerm.toLowerCase();
                                            return (
                                                (integration.name || "")
                                                    .toLowerCase()
                                                    .includes(q) ||
                                                (integration.category || "")
                                                    .toLowerCase()
                                                    .includes(q) ||
                                                (integration.id || "")
                                                    .toLowerCase()
                                                    .includes(q)
                                            );
                                        })
                                        .map((integration) => (
                                            <DropdownMenuItem
                                                key={integration.id}
                                                onClick={() =>
                                                    handleIntegrationSelect(
                                                        integration
                                                    )
                                                }
                                                className="flex items-center gap-3 cursor-pointer"
                                            >
                                                {integration.imageIcon ? (
                                                    <img
                                                        src={
                                                            integration.imageIcon
                                                        }
                                                        alt={integration.name}
                                                        className="h-5 w-5"
                                                    />
                                                ) : (
                                                    <Icon
                                                        name={integration.icon}
                                                        className="h-5 w-5"
                                                    />
                                                )}
                                                <div>
                                                    <p className="font-medium">
                                                        {integration.name}
                                                    </p>
                                                    <p className="text-xs text-gray-500">
                                                        {integration.category}
                                                    </p>
                                                </div>
                                                <Badge
                                                    variant="secondary"
                                                    className="ml-auto"
                                                >
                                                    <Check className="h-3 w-3" />
                                                </Badge>
                                            </DropdownMenuItem>
                                        ))}
                                    <DropdownMenuSeparator />
                                </>
                            )}

                            <DropdownMenuLabel>
                                {t("chatInput.availableIntegrations")}
                            </DropdownMenuLabel>
                            {availableIntegrations
                                .filter((integration) => {
                                    const q =
                                        integrationsSearchTerm.toLowerCase();
                                    return (
                                        (integration.name || "")
                                            .toLowerCase()
                                            .includes(q) ||
                                        (integration.category || "")
                                            .toLowerCase()
                                            .includes(q) ||
                                        (integration.id || "")
                                            .toLowerCase()
                                            .includes(q)
                                    );
                                })
                                .map((integration) => (
                                    <DropdownMenuItem
                                        key={integration.id}
                                        onClick={() =>
                                            handleIntegrationSelect(integration)
                                        }
                                        className="flex items-center gap-3 cursor-pointer"
                                    >
                                        {integration.imageIcon ? (
                                            <img
                                                src={integration.imageIcon}
                                                alt={integration.name}
                                                className="h-5 w-5"
                                            />
                                        ) : (
                                            <Icon
                                                name={integration.icon}
                                                className="h-5 w-5"
                                            />
                                        )}
                                        <div>
                                            <p className="font-medium">
                                                {integration.name}
                                            </p>
                                            <p className="text-xs text-gray-500">
                                                {integration.category}
                                            </p>
                                        </div>
                                        {integration.hasOAuth && (
                                            <ExternalLink className="h-3 w-3 ml-auto text-gray-400" />
                                        )}
                                    </DropdownMenuItem>
                                ))}
                        </DropdownMenuContent>
                    </DropdownMenu>
                </motion.div>
            )}

            {/* OAuth Integration Dialog */}
            <Dialog open={showOAuthDialog} onOpenChange={setShowOAuthDialog}>
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="flex items-center gap-3">
                            {selectedIntegration?.imageIcon ? (
                                <img
                                    src={selectedIntegration.imageIcon}
                                    alt={selectedIntegration?.name}
                                    className="h-6 w-6"
                                />
                            ) : (
                                <Icon
                                    name={selectedIntegration?.icon || "Puzzle"}
                                    className="h-6 w-6"
                                />
                            )}
                            Connect {selectedIntegration?.name}
                        </DialogTitle>
                        <DialogDescription>
                            {selectedIntegration?.hasOAuth
                                ? `You'll be redirected to ${selectedIntegration?.name} to authorize the connection. This is secure and you can revoke access at any time.`
                                : `Configure your ${selectedIntegration?.name} integration by providing the required credentials.`}
                        </DialogDescription>
                    </DialogHeader>

                    <div className="flex flex-col gap-4">
                        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                            <div className="flex items-start gap-3">
                                <Settings className="h-5 w-5 text-blue-600 mt-0.5" />
                                <div>
                                    <h4 className="font-medium text-blue-900">
                                        What you can do:
                                    </h4>
                                    <ul className="text-sm text-blue-700 mt-1 space-y-1">
                                        <li>• Automate data workflows</li>
                                        <li>
                                            • Sync information across platforms
                                        </li>
                                        <li>• Create custom automations</li>
                                    </ul>
                                </div>
                            </div>
                        </div>

                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={() => setShowOAuthDialog(false)}
                                className="flex-1"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={handleOAuthFlow}
                                className="flex-1"
                            >
                                {selectedIntegration?.hasOAuth ? (
                                    <>
                                        <ExternalLink className="h-4 w-4 mr-2" />
                                        Authorize
                                    </>
                                ) : (
                                    "Configure"
                                )}
                            </Button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
            {/* {Bluelagoon?.isDevelopment && workflow?.publicKey ? (
                <div>
                    <Button
                        onClick={() => {
                            navigator.clipboard.writeText(workflow?.publicKey);
                            toast.success("Copied to clipboard!");
                        }}
                        className="h-11 px-3 hover:bg-gray-100 border-none shadow-none text-xs text-center w-full mt-5"
                    >
                        DEBUG PUBLIC KEY: {workflow?.publicKey}
                    </Button>
                </div>
            ) : null} */}

            {!hideActionButtons && activeDatabase && (
                <div className="flex items-center justify-center gap-2 text-xs text-slate-600 mt-2 max-w-full overflow-hidden hidden lg:block">
                    <div className="flex items-center gap-2 min-w-0">
                        <div className="w-2 h-2 rounded-full bg-red-500 animate-pulse flex-shrink-0"></div>
                        <span className="truncate">
                            Active Database: {activeDatabase.name}
                            {activeDatabase.tables &&
                                activeDatabase.tables.length > 0 && (
                                    <span className="text-slate-500">
                                        {" "}
                                        (
                                        {activeDatabase.tables.map(
                                            (table, idx) => (
                                                <React.Fragment
                                                    key={table.id || idx}
                                                >
                                                    {idx > 0 && ", "}
                                                    <Link
                                                        to={`/databases/${activeDatabase.id}/tables/${table.id}`}
                                                        className="hover:text-blue-600 hover:underline transition-colors"
                                                    >
                                                        {table.name}
                                                    </Link>
                                                </React.Fragment>
                                            )
                                        )}
                                        )
                                    </span>
                                )}
                        </span>
                    </div>
                </div>
            )}

            {/* New Integration Modal */}
            {showNewIntegrationModal && (
                <NewIntegrationModal
                    onClose={handleNewIntegrationClose}
                    onSuccess={handleNewIntegrationSuccess}
                    initialIntegrationType={selectedIntegrationForSetup}
                    INTEGRATION_TYPES={INTEGRATION_TYPES}
                />
            )}

            {/* WhatsApp Embedded Modal */}
            {showWhatsAppModal && (
                <WhatsAppEmbeddedModal
                    onClose={() => setShowWhatsAppModal(false)}
                    onSuccess={handleNewIntegrationSuccess}
                />
            )}

            {/* Authentication Modal */}
        </>
    );
}
